import HotelPlanner, { Room } from '../../libs/gs-component-hotel-planner';
import React, { useContext, useMemo, useState } from 'react';
import { Action } from '../Card';

import HotelPlannerLogo from './HotelPlannerLogo';
import isEmpty from 'lodash/isEmpty';
import styles from './index.scss';
import addItemToPage from '../../helpers/apolloCache/addItemToPage';
import { getHotelIds } from '../../helpers/getHotelPlannerIds';
import { getHpLocation } from '../../helpers/getHpLocation';
import { mapRoomToPageItem } from '../../helpers/mapRoomToPageItem';
import { useEventsQuery } from '../../generated/graphql';
import { useImageUploader } from '../../helpers/images/useImageUploader';
import {
  AccommodationItemsFragment,
  PagesQuery,
  useCreateAccommodationItemMutation,
  useWeddingWebsiteQuery,
} from '../../generated/graphql';
import CVAlert from '../CVAlert';
import Honeybadger from 'honeybadger-js';
import { HotelPlannerTooltip } from '../TansientRoomRailCard';
import { TrackingContext } from '../../TrackingContext';
import { useGetHotelEngineUrl } from '../../hooks/useGetHotelEngineUrl';

type AccommodationItem = AccommodationItemsFragment | undefined;
type Page = PagesQuery['pages'][0];

interface Props {
  page: Page;
}

const HotelPlannerCalloutPC = () => {
  const { getHotelEngineUrl } = useGetHotelEngineUrl();
  const hotelEngineUrl = getHotelEngineUrl();
  const { track } = useContext(TrackingContext);

  const onClickLink = () => {
    track('Item Action Initiated', {
      action: 'hotel_added',
      actionName: 'hotel_added_link',
    });

    window.open(hotelEngineUrl);
  };

  return (
    <div data-testid="hotel-planner-callout" className={styles.hotelPlannerActionWrapper}>
      <Action iconName="linkout" onClick={onClickLink} className={styles.hotelPlannerLink} rel="sponsored">
        <span className={styles.hotelPlanerLinkText}>Browse Hotel Room Blocks</span>
        <div className={styles.hotelPlannerLogoContainer}>
          <HotelPlannerLogo />
          <HotelPlannerTooltip />
        </div>
      </Action>
    </div>
  );
};

const RoomBlockAndTransient = ({ page }: Props) => {
  const { data } = useWeddingWebsiteQuery();
  const hotelPlanerIds = useMemo(() => getHotelIds(page.items as AccommodationItemsFragment[]), [page.items]);
  const [createAccommodation] = useCreateAccommodationItemMutation();
  const { data: eventsData } = useEventsQuery();
  const [erroring, setErroring] = useState(false);

  const weddingLocation: string | undefined | null = useMemo(
    () => getHpLocation(eventsData?.events, data?.weddingWebsite?.weddingLocation),
    [data, eventsData]
  );
  const { imageUpload } = useImageUploader();

  const addTransientRoom = async (room: Room) => {
    try {
      const res = await imageUpload({ imageUrl: room.imagePath });
      const photo = res.data;

      await createAccommodation({
        variables: {
          attributes: mapRoomToPageItem(page.id, room, photo),
        },
        update(cache, { data }) {
          addItemToPage<AccommodationItem>(cache, page.id, data?.item);
        },
      });
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error adding transient room',
        context: {
          error,
        },
      });
      setErroring(true);
    }
  };

  return (
    <>
      <HotelPlannerCalloutPC />
      {data?.wedding?.weddingDate && !isEmpty(weddingLocation) && (
        <>
          <CVAlert erroring={erroring} onTimeout={() => setErroring(false)} />
          <HotelPlanner
            weddingDate={data.wedding.weddingDate}
            weddingLocation={weddingLocation || ''}
            onClick={addTransientRoom}
            exclude={hotelPlanerIds}
            newVersion
          />
        </>
      )}
    </>
  );
};

export default RoomBlockAndTransient;
